import { useCallback, useEffect, useState } from 'react';
import { supabase, type Database } from '../lib/supabase';

type Tables = Database['public']['Tables'];

// Hook pour accéder au client Supabase
export function useSupabase() {
  return supabase;
}

// Hook générique pour récupérer des données d'une table
export function useSupabaseQuery<T extends keyof Tables>(
  table: T,
  options?: {
    select?: string;
    filter?: (query: any) => any;
    orderBy?: { column: string; ascending?: boolean };
  }
) {
  const [data, setData] = useState<Tables[T]['Row'][]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Vérifier que l'utilisateur est connecté
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Utilisateur non connecté');
      }

      let query = supabase.from(table).select(options?.select || '*');

      if (options?.filter) {
        query = options.filter(query);
      }

      if (options?.orderBy) {
        query = query.order(options.orderBy.column, {
          ascending: options.orderBy.ascending ?? true,
        });
      }

      const { data: result, error } = await query;

      if (error) throw error;
      setData((result as unknown as Tables[T]['Row'][]) || []);
    } catch (err) {
      setError(err as Error);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [table, options]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const refetch = useCallback(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch };
}

// Hook spécifique pour les contacts
export function useContacts(orgId?: string) {
  return useSupabaseQuery('contacts', {
    filter: orgId ? (query) => query.eq('org_id', orgId) : undefined,
    orderBy: { column: 'created_at', ascending: false },
  });
}

// Hook spécifique pour les équipages
export function useEquipages(orgId?: string) {
  return useSupabaseQuery('equipages', {
    filter: orgId ? (query) => query.eq('org_id', orgId) : undefined,
    orderBy: { column: 'created_at', ascending: false },
  });
}

// Hook spécifique pour les interactions
export function useInteractions(orgId?: string, contactId?: string, equipageId?: string) {
  return useSupabaseQuery('interactions', {
    filter: (query) => {
      let filteredQuery = query;
      if (orgId) filteredQuery = filteredQuery.eq('org_id', orgId);
      if (contactId) filteredQuery = filteredQuery.eq('contact_id', contactId);
      if (equipageId) filteredQuery = filteredQuery.eq('equipage_id', equipageId);
      return filteredQuery;
    },
    orderBy: { column: 'happened_at', ascending: false },
  });
}

// Hook spécifique pour les tâches
export function useTasks(orgId?: string, doneFilter?: boolean) {
  return useSupabaseQuery('tasks', {
    filter: (query) => {
      let filteredQuery = query;
      if (orgId) filteredQuery = filteredQuery.eq('org_id', orgId);
      if (typeof doneFilter === 'boolean') filteredQuery = filteredQuery.eq('done', doneFilter);
      return filteredQuery;
    },
    orderBy: { column: 'due_at', ascending: true },
  });
}

// Hook pour récupérer les organisations de l'utilisateur
export function useUserOrgs() {
  return useSupabaseQuery('memberships', {
    select: 'role, orgs(id, name, created_at)',
    orderBy: { column: 'org_id', ascending: true },
  });
}
